<script>
import {ref, computed, watch, onMounted} from "vue";
import {useDataSelectStore} from "@/stores/DataSelect.js";
import {useSecurityStore} from "@/stores/Security.js";
import useSharedData from "@/use/SharedData.js";
import useTools from "@/use/Tools.js";
import useValidation from "@/use/Validation.js";
import useSharedMethod from "@/use/SharedMethod";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration},

    props: {
        value: {type: [Number, Array], default: null},
        type: {type: String, required: true},
        multiple: {type: Boolean, default: false},
        selectDefault: {type: Boolean, default: false},
        label: {type: String, default: ""},
        color: {type: String, default: ""},
        disabled: {type: Boolean, default: false},
        readonly: {type: Boolean, default: false},
        notClearable: {type: Boolean, default: false},
        showDefaultLabel: {type: Boolean, default: false},
        rules: {type: Array, default: () => []},
        hint: {type: String, default: ""},
        persistentHint: {type: Boolean, default: false},
    },

    setup(props, context) {
        const {$refs} = useTools();
        const {transformFilterValues} = useSharedMethod();
        const {mxRules} = useValidation();
        const {filter, loading, inputColor, backgroundColor, dialog} = useSharedData();

        const dataSelectStore = useDataSelectStore();
        const securityStore = useSecurityStore();

        dialog.value = false;
        const dataSelect = ref(props.multiple ? [] : null);
        const dataSelects = ref([]);
        const entity = ref({primary: false, type: props.type});

        // ── watchers ──────────────────────────────────────────────────────────
        watch(() => props.value, (val) => {
            dataSelect.value = val;
        }, {immediate: true});

        watch(() => props.type, () => {
            findAll();
        }, {immediate: true});

        // ── select-all helpers ────────────────────────────────────────────────
        const allSelected = computed(() =>
            dataSelects.value.length > 0 &&
            Array.isArray(dataSelect.value) &&
            dataSelect.value.length === dataSelects.value.length
        );

        const someSelected = computed(() =>
            Array.isArray(dataSelect.value) &&
            dataSelect.value.length > 0 &&
            !allSelected.value
        );

        function toggleAll() {
            dataSelect.value = allSelected.value
                ? []
                : dataSelects.value.map((i) => i.id);
            context.emit("input", dataSelect.value);
        }

        function clearAll() {
            dataSelect.value = [];
            context.emit("input", dataSelect.value);
        }

        function removeItem(item) {
            dataSelect.value = dataSelect.value.filter((id) => id !== item.id);
            context.emit("input", dataSelect.value);
        }

        function onInput() {
            context.emit("input", dataSelect.value);
        }

        // ── API ───────────────────────────────────────────────────────────────
        function findAll() {
            loading.value = true;
            filter.value.allRecords = true;
            filter.value.type = props.type;
            axios
                .get(`/dataSelects/type/${props.type}`)
                .then((response) => {
                    dataSelectStore.setDataSelectsSelect(response.data, props.type);
                    dataSelects.value = response.data;
                })
                .finally(() => {
                    loading.value = false;
                });
        }

        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post("/dataSelects", entity.value).then((response) => {
                    dataSelectStore.addDataSelectsSelect(response.data, props.type);
                    dataSelects.value.push(response.data);
                    if (props.multiple) {
                        dataSelect.value.push(response.data.id);
                    } else {
                        dataSelect.value = response.data.id;
                    }
                    context.emit("input", dataSelect.value);
                    dialog.value = false;
                    entity.value = {primary: false, type: props.type};
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => findAll());

        return {
            dataSelectStore,
            securityStore,
            dataSelect,
            dataSelects,
            loading,
            dialog,
            inputColor,
            backgroundColor,
            entity,
            mxRules,
            allSelected,
            someSelected,
            toggleAll,
            clearAll,
            removeItem,
            onInput,
            save,
        };
    },
};
</script>

<template>
    <div>
        <!-- ── Add-new dialog ──────────────────────────────────────────────── -->
        <v-dialog v-model="dialog" max-width="600">
            <v-card>
                <w-toolbar
                    color="teal"
                    :title="`${$t('common.add')} ${$t(`data_select.${type}`)}`"
                />
                <v-card-text>
                    <v-container>
                        <v-form ref="form" lazy-validation @submit.prevent="1">
                            <v-row>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="entity.code"
                                        entity="DATA_SELECT"
                                        load
                                        :rules="[mxRules.required]"
                                        :color="color"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.name')" required/>
                                    <w-text-field
                                        v-model="entity.name"
                                        :rules="[mxRules.required]"
                                        :color="color"
                                    />
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>

                    <v-divider/>

                    <v-card-actions>
                        <v-spacer/>
                        <w-btn-close @press="dialog = false"/>
                        <w-btn-add show :loading="loading" @press="save"/>
                    </v-card-actions>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- ── Autocomplete ───────────────────────────────────────────────── -->
        <v-autocomplete
            v-model="dataSelect"
            :label="showDefaultLabel ? $t(`data_select.${type}`) : label"
            :items="dataSelects"
            item-value="id"
            item-text="name"
            outlined
            dense
            placeholder="Veuillez sélectionner"
            :clearable="!readonly && !multiple && !notClearable"
            :chips="multiple"
            :deletable-chips="multiple"
            :color="color ? color : inputColor"
            :background-color="color ? color + ' lighten-5' : backgroundColor"
            hide-details="auto"
            :loading="loading"
            :disabled="disabled"
            :readonly="readonly"
            :multiple="multiple"
            :rules="rules"
            :hint="hint"
            :persistent-hint="persistentHint"
            :prepend-inner-icon="
                securityStore.authorities['UPDATE_GENERAL_PARAM'] && !readonly
                    ? 'mdi-plus-circle-outline'
                    : ''
            "
            @input="onInput"
            @click:prepend-inner="dialog = true"
        >
            <!-- Select-all row — only when multiple -->
            <template v-if="multiple" #prepend-item>
                <v-list-item ripple @mousedown.prevent @click="toggleAll">
                    <v-list-item-action>
                        <v-icon :color="(allSelected || someSelected) ? (color || 'teal') : ''">
                            {{
                                allSelected ? 'mdi-checkbox-marked' :
                                    someSelected ? 'mdi-minus-box' :
                                        'mdi-checkbox-blank-outline'
                            }}
                        </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title class="font-weight-medium">
                            Tous
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-0 mb-0"/>
            </template>

            <!-- Custom chip rendering — show single "Tous" chip when all selected -->
            <template v-if="multiple" #selection="{ item, index }">
                <!-- All selected: render one "Tous" chip on index 0, skip the rest -->
                <template v-if="allSelected">
                    <v-chip
                        v-if="index === 0"
                        close
                        small
                        :color="color ? color : 'teal'"
                        dark
                        @click:close="clearAll"
                    >
                        Tous
                    </v-chip>
                </template>

                <!-- Partial selection: render individual chips normally -->
                <v-chip
                    v-else
                    close
                    small
                    @click:close="removeItem(item)"
                >
                    {{ item.name }}
                </v-chip>
            </template>
        </v-autocomplete>
    </div>
</template>